import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionIcon from "@mui/icons-material/Description";
// import { submitReconciliation } from "../billingReconciliationApi"; // Uncomment when API is ready

const ReconciliationModal = ({ open, onClose, airtelData, bsnlData, currentMonth = "December 2025" }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [manualAmount, setManualAmount] = useState("");
  const [reconciliationComplete, setReconciliationComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionNotes, setActionNotes] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const steps = ["Upload Bill", "Compare", "Review Differences", "Take Action"];

  // Mock plan-wise comparison data
  const airtelPlanComparison = [
    { planName: "RS.45 Monthly Plan", projectedSims: 250, actualSims: 280, projectedBill: 11250, actualBill: 12600, difference: 1350 },
    { planName: "RS.35 Monthly Plan", projectedSims: 400, actualSims: 400, projectedBill: 28000, actualBill: 28000, difference: 0 },
    { planName: "Premium Plan", projectedSims: 350, actualSims: 350, projectedBill: 45500, actualBill: 46380, difference: 880 },
  ];

  const bsnlPlanComparison = [
    { planName: "Economy Plan", projectedSims: 300, actualSims: 300, projectedBill: 12000, actualBill: 12000, difference: 0 },
    { planName: "Business Plan", projectedSims: 450, actualSims: 450, projectedBill: 31500, actualBill: 31500, difference: 0 },
    { planName: "Enterprise Plan", projectedSims: 250, actualSims: 250, projectedBill: 27500, actualBill: 27500, difference: 0 },
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleProviderSelect = (provider) => {
    setSelectedProvider(provider);
    setActiveStep(0);
    setUploadedFile(null);
    setManualAmount("");
    setReconciliationComplete(false);
    setActionNotes("");
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      setReconciliationComplete(true);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const getProviderData = () => {
    if (selectedProvider === "Airtel") {
      return { data: airtelData, plans: airtelPlanComparison, color: "#dc2626" };
    }
    return { data: bsnlData, plans: bsnlPlanComparison, color: "#2563eb" };
  };

  const calculateDiscrepancy = (data) => {
    const diff = data.actualBillReceived - data.projectedTotal;
    const percentage = ((diff / data.projectedTotal) * 100).toFixed(1);
    return { diff, percentage };
  };

  // Handle action submission (Accept/Dispute/Credit)
  const handleActionSubmit = async (actionType) => {
    setIsSubmitting(true);
    
    const { data, plans } = getProviderData();
    const { diff, percentage } = calculateDiscrepancy(data);

    const submissionData = {
      provider: selectedProvider,
      billingMonth: currentMonth,
      projectedAmount: data.projectedTotal,
      actualAmount: data.actualBillReceived,
      discrepancy: diff,
      discrepancyPct: parseFloat(percentage),
      action: actionType, // 'accept', 'dispute', 'credit'
      planDetails: plans,
      uploadedFile: uploadedFile,
      notes: actionNotes,
    };

    try {
      // Option 1: Call real API (uncomment when backend is ready)
      // const response = await submitReconciliation(submissionData);
      
      // Option 2: Mock submission for development
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API delay
      
      console.log("Reconciliation submitted:", submissionData);

      setReconciliationComplete(true);
      
      // Show success message based on action
      let successMessage = "";
      switch (actionType) {
        case "accept":
          successMessage = `Bill accepted for ${selectedProvider}. Reconciliation marked as complete.`;
          break;
        case "dispute":
          successMessage = `Dispute raised with ${selectedProvider} for ₹${Math.abs(diff).toLocaleString()}. Ticket created.`;
          break;
        case "credit":
          successMessage = `Credit request of ₹${Math.abs(diff).toLocaleString()} sent to ${selectedProvider}.`;
          break;
        default:
          successMessage = "Reconciliation completed successfully.";
      }
      
      setSnackbar({ open: true, message: successMessage, severity: "success" });
      
    } catch (error) {
      console.error("Error submitting reconciliation:", error);
      setSnackbar({ 
        open: true, 
        message: "Failed to submit reconciliation. Please try again.", 
        severity: "error" 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setSelectedProvider(null);
    setActiveStep(0);
    setUploadedFile(null);
    setManualAmount("");
    setReconciliationComplete(false);
    setActionNotes("");
    onClose();
  };

  // Provider Selection Screen
  const ProviderSelectionScreen = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={3} textAlign="center">
        Select Provider to Reconcile
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box
            onClick={() => handleProviderSelect("Airtel")}
            sx={{
              border: "2px solid #fecaca",
              borderRadius: "12px",
              p: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { boxShadow: 4, transform: "translateY(-4px)", borderColor: "#dc2626" },
            }}
          >
            <Typography variant="h5" fontWeight={800} color="#dc2626" mb={2}>
              📱 Airtel
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Your Projected Bill:</Typography>
              <Typography fontWeight={700}>₹{airtelData.projectedTotal.toLocaleString()}</Typography>
            </Box>
            <Typography fontSize="12px" color="text.secondary" mt={2}>
              Click to upload actual bill and reconcile
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box
            onClick={() => handleProviderSelect("BSNL")}
            sx={{
              border: "2px solid #bfdbfe",
              borderRadius: "12px",
              p: 3,
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { boxShadow: 4, transform: "translateY(-4px)", borderColor: "#2563eb" },
            }}
          >
            <Typography variant="h5" fontWeight={800} color="#2563eb" mb={2}>
              📡 BSNL
            </Typography>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography color="text.secondary">Your Projected Bill:</Typography>
              <Typography fontWeight={700}>₹{bsnlData.projectedTotal.toLocaleString()}</Typography>
            </Box>
            <Typography fontSize="12px" color="text.secondary" mt={2}>
              Click to upload actual bill and reconcile
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  // Step 1: Upload Bill
  const UploadBillStep = () => (
    <Box>
      <Typography variant="h6" fontWeight={700} mb={3}>
        Step 1: Upload {selectedProvider} Invoice
      </Typography>
      
      {/* File Upload Area */}
      <Box
        sx={{
          border: "2px dashed #d1d5db",
          borderRadius: "12px",
          p: 4,
          textAlign: "center",
          mb: 3,
          cursor: "pointer",
          backgroundColor: uploadedFile ? "#dcfce7" : "#f9fafb",
          "&:hover": { backgroundColor: "#f3f4f6", borderColor: "#9ca3af" },
        }}
        component="label"
      >
        <input
          type="file"
          hidden
          accept=".pdf,.xlsx,.xls,.csv"
          onChange={handleFileUpload}
        />
        {uploadedFile ? (
          <>
            <DescriptionIcon sx={{ fontSize: 48, color: "#16a34a", mb: 1 }} />
            <Typography fontWeight={700} color="#16a34a">
              {uploadedFile.name}
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              Click to change file
            </Typography>
          </>
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 48, color: "#9ca3af", mb: 1 }} />
            <Typography fontWeight={600} mb={1}>
              Click to upload invoice
            </Typography>
            <Typography fontSize="12px" color="text.secondary">
              PDF, Excel, CSV accepted
            </Typography>
          </>
        )}
      </Box>

      <Divider sx={{ my: 3 }}>
        <Typography color="text.secondary">OR</Typography>
      </Divider>

      {/* Manual Entry */}
      <Box>
        <Typography fontWeight={600} mb={2}>
          Enter Bill Amount Manually
        </Typography>
        <TextField
          fullWidth
          label="Actual Bill Amount (₹)"
          type="number"
          value={manualAmount}
          onChange={(e) => setManualAmount(e.target.value)}
          placeholder="e.g., 86980"
          InputProps={{
            startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
          }}
        />
      </Box>
    </Box>
  );

  // Step 2: Compare
  const CompareStep = () => {
    const { data, color } = getProviderData();
    const { diff, percentage } = calculateDiscrepancy(data);
    const hasDiscrepancy = diff !== 0;

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} mb={3}>
          Step 2: Bill Comparison - {selectedProvider}
        </Typography>

        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ backgroundColor: "#eff6ff", borderRadius: "12px", p: 3 }}>
              <Typography fontSize="14px" color="text.secondary" mb={1}>
                Your Calculated Bill
              </Typography>
              <Typography fontSize="36px" fontWeight={800} color="#2563eb">
                ₹{data.projectedTotal.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ backgroundColor: hasDiscrepancy ? "#fee2e2" : "#dcfce7", borderRadius: "12px", p: 3 }}>
              <Typography fontSize="14px" color="text.secondary" mb={1}>
                Provider's Actual Bill
              </Typography>
              <Typography fontSize="36px" fontWeight={800} color={hasDiscrepancy ? "#dc2626" : "#16a34a"}>
                ₹{data.actualBillReceived.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Comparison Result */}
        <Box
          sx={{
            backgroundColor: hasDiscrepancy ? "#fee2e2" : "#dcfce7",
            border: `2px solid ${hasDiscrepancy ? "#fca5a5" : "#86efac"}`,
            borderRadius: "12px",
            p: 3,
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              {hasDiscrepancy ? (
                <WarningIcon sx={{ color: "#dc2626", fontSize: 40, mr: 2 }} />
              ) : (
                <CheckCircleIcon sx={{ color: "#16a34a", fontSize: 40, mr: 2 }} />
              )}
              <Box>
                <Typography variant="h6" fontWeight={800} color={hasDiscrepancy ? "#991b1b" : "#166534"}>
                  {hasDiscrepancy ? "⚠️ DISCREPANCY FOUND" : "✅ BILLS MATCHED PERFECTLY"}
                </Typography>
                <Typography fontSize="14px">
                  {hasDiscrepancy 
                    ? `You are being ${diff > 0 ? "overcharged" : "undercharged"} by ${Math.abs(percentage)}%`
                    : "No discrepancies found between projected and actual bills"
                  }
                </Typography>
              </Box>
            </Box>
            <Typography fontSize="36px" fontWeight={800} color={hasDiscrepancy ? "#dc2626" : "#16a34a"}>
              {diff > 0 ? "+" : ""}₹{diff.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Step 3: Drill Down - Plan-wise differences
  const DrillDownStep = () => {
    const { plans, color } = getProviderData();

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} mb={3}>
          Step 3: Plan-wise Breakdown - {selectedProvider}
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f3f4f6" }}>
                <TableCell sx={{ fontWeight: 700 }}>Plan Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Projected SIMs</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actual SIMs</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Projected Bill</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actual Bill</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Difference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan, index) => (
                <TableRow 
                  key={index}
                  sx={{ 
                    backgroundColor: plan.difference !== 0 ? "#fef2f2" : "transparent",
                    "&:hover": { backgroundColor: "#f9fafb" }
                  }}
                >
                  <TableCell>
                    <Typography fontWeight={600}>{plan.planName}</Typography>
                  </TableCell>
                  <TableCell align="center">{plan.projectedSims}</TableCell>
                  <TableCell align="center">
                    <Typography 
                      fontWeight={plan.projectedSims !== plan.actualSims ? 700 : 400}
                      color={plan.projectedSims !== plan.actualSims ? "#dc2626" : "inherit"}
                    >
                      {plan.actualSims}
                      {plan.projectedSims !== plan.actualSims && (
                        <Typography component="span" fontSize="12px" ml={1}>
                          (+{plan.actualSims - plan.projectedSims})
                        </Typography>
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">₹{plan.projectedBill.toLocaleString()}</TableCell>
                  <TableCell align="right">₹{plan.actualBill.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {plan.difference !== 0 ? (
                      <Chip 
                        label={`+₹${plan.difference.toLocaleString()}`} 
                        color="error" 
                        size="small"
                        sx={{ fontWeight: 700 }}
                      />
                    ) : (
                      <Chip label="₹0" color="success" size="small" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary of Issues */}
        {plans.some(p => p.difference !== 0) && (
          <Box sx={{ backgroundColor: "#fef3c7", borderRadius: "12px", p: 2, mt: 3 }}>
            <Typography fontWeight={700} mb={1}>💡 Identified Issues:</Typography>
            {plans.filter(p => p.difference !== 0).map((plan, idx) => (
              <Typography key={idx} fontSize="14px">
                • {plan.planName}: Extra charge of ₹{plan.difference.toLocaleString()}
                {plan.projectedSims !== plan.actualSims && ` (${plan.actualSims - plan.projectedSims} extra SIMs charged)`}
              </Typography>
            ))}
          </Box>
        )}
      </Box>
    );
  };

  // Step 4: Take Action
  const TakeActionStep = () => {
    const { data, color } = getProviderData();
    const { diff } = calculateDiscrepancy(data);
    const hasDiscrepancy = diff !== 0;

    return (
      <Box>
        <Typography variant="h6" fontWeight={700} mb={3}>
          Step 4: Take Action - {selectedProvider}
        </Typography>

        {reconciliationComplete ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            Reconciliation completed successfully! Your action has been recorded.
          </Alert>
        ) : isSubmitting ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={60} />
            <Typography mt={2} fontWeight={600}>
              Submitting your action...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Notes Field */}
            <TextField
              fullWidth
              label="Add Notes (Optional)"
              multiline
              rows={2}
              value={actionNotes}
              onChange={(e) => setActionNotes(e.target.value)}
              placeholder="Add any additional notes for this reconciliation..."
              sx={{ mb: 3 }}
            />

            {hasDiscrepancy ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      border: "2px solid #86efac",
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#dcfce7" },
                    }}
                    onClick={() => handleActionSubmit("accept")}
                  >
                    <CheckCircleIcon sx={{ fontSize: 48, color: "#16a34a", mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="#166534">
                      Accept Bill
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary">
                      Accept the provider's bill as correct
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      border: "2px solid #fca5a5",
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#fee2e2" },
                    }}
                    onClick={() => handleActionSubmit("dispute")}
                  >
                    <WarningIcon sx={{ fontSize: 48, color: "#dc2626", mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="#991b1b">
                      Raise Dispute
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary">
                      Dispute the overcharge of ₹{Math.abs(diff).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      border: "2px solid #c4b5fd",
                      borderRadius: "12px",
                      p: 3,
                      textAlign: "center",
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#ede9fe" },
                    }}
                    onClick={() => handleActionSubmit("credit")}
                  >
                    <CompareArrowsIcon sx={{ fontSize: 48, color: "#7c3aed", mb: 1 }} />
                    <Typography variant="h6" fontWeight={700} color="#5b21b6">
                      Request Credit
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary">
                      Request credit for next month's bill
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CheckCircleIcon sx={{ fontSize: 64, color: "#16a34a", mb: 2 }} />
                <Typography variant="h5" fontWeight={700} color="#166534" mb={1}>
                  No Action Required
                </Typography>
                <Typography color="text.secondary">
                  Your projected bill matches the actual bill. Reconciliation is complete.
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 3, backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
                  onClick={() => handleActionSubmit("matched")}
                >
                  Mark as Reconciled
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    );
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <UploadBillStep />;
      case 1:
        return <CompareStep />;
      case 2:
        return <DrillDownStep />;
      case 3:
        return <TakeActionStep />;
      default:
        return null;
    }
  };

  return (
    <>
      <Dialog open={open} onClose={resetAndClose} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle sx={{ fontWeight: 800, fontSize: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box display="flex" alignItems="center">
            <Typography fontSize="28px" mr={2}>🔍</Typography>
            Bill Reconciliation
          </Box>
          <IconButton onClick={resetAndClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!selectedProvider ? (
            <ProviderSelectionScreen />
          ) : (
            <Box>
              {/* Back to Provider Selection */}
              <Button 
                variant="text" 
                onClick={() => setSelectedProvider(null)} 
                sx={{ mb: 2 }}
              >
                ← Back to Provider Selection
              </Button>

              {/* Stepper */}
              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* Step Content */}
              {renderStepContent()}
            </Box>
          )}
        </DialogContent>

        {selectedProvider && (
          <DialogActions sx={{ p: 3, justifyContent: "space-between" }}>
            <Button
              variant="outlined"
              onClick={activeStep === 0 ? () => setSelectedProvider(null) : handleBack}
              disabled={reconciliationComplete || isSubmitting}
            >
              {activeStep === 0 ? "Cancel" : "Back"}
            </Button>
            <Button
              variant="contained"
              onClick={reconciliationComplete ? resetAndClose : handleNext}
              disabled={(activeStep === 0 && !uploadedFile && !manualAmount) || isSubmitting}
              sx={{
                backgroundColor: reconciliationComplete ? "#16a34a" : "#2563eb",
                "&:hover": { backgroundColor: reconciliationComplete ? "#15803d" : "#1d4ed8" },
              }}
            >
              {reconciliationComplete 
                ? "Close" 
                : activeStep === steps.length - 1 
                  ? "Complete Reconciliation" 
                  : "Next"
              }
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ReconciliationModal;